import { keyframes } from "@emotion/react";
import { Box, Button, Icon, Image, Typography } from "@outschool/backpack";
import { REFER_CREDIT_CENTS } from "@outschool/business-rules";
import * as FilestackUrls from "@outschool/filestack-urls";
import {
  Activity as ActivityType,
  Leader,
  Section as SectionType,
} from "@outschool/gql-backend-generated";
import {
  ActivityListing_ListingFragment,
  ListingCard_ListingFragment,
  TeacherPublicProfileQueryQuery,
  TeacherPublicProfileQueryQueryVariables,
} from "@outschool/gql-frontend-generated";
import { faCalendarAlt, faEnvelope, faShare } from "@outschool/icons";
import {
  Trans,
  renderCurrencyString,
  useTranslation,
} from "@outschool/localization";
import {
  addParamsToUrl,
  createPathWithActivity,
  leaderUrl,
  pathFromLocation,
} from "@outschool/routes";
import {
  IntegrationCategory,
  useAnalytics,
  useProductClicked,
  useProductListViewed,
  useTrackEvent,
} from "@outschool/ui-analytics";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { AttachmentFragment } from "@outschool/ui-components-classroom";
import { NavTabs } from "@outschool/ui-components-shared";
import {
  ActivityListingFragment,
  HelpLink,
  LeaderFragment,
  OutreachModal,
  OutreachModalType,
  PublicReviewsOfLeaderModal,
  useIsTeacherFollowedByCurrentUser,
} from "@outschool/ui-components-website";
import {
  Loading,
  TrackingPage,
  useImpressionTracking,
} from "@outschool/ui-legacy-component-library";
import { Screen, useNavigation, useScrollToElement } from "@outschool/ui-utils";
import _ from "lodash";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router";

import * as helpers from "../../shared/helpers";
import * as Routes from "../../shared/Routes";
import SimpleLayout from "../components/layouts/SimpleLayout";
import Link from "../components/Link";
import {
  ActivityListing,
  ActivityTrackingProps,
  scheduleText,
} from "../components/listings/ActivityListing";
// @ts-ignore - JSX import
import MessageModal from "../components/MessageModal";
import PageModal from "../components/PageModal";
import { PublicReviewsOfLeader } from "../components/reviews";
import { ReviewRespondButton } from "../components/reviews/ReviewRespondButton";
import { FollowButton } from "../components/saving/FollowButton";
import { useIsActivitySavedByCurrentUser } from "../components/saving/graphql/hooks";
import { SaveHeart } from "../components/saving/SaveButton";
import { ViewSelector, ViewSelectorName } from "../components/ScheduleCalendar";
import OutReachOrGive20Get20 from "../components/sharing/OutReachOrGive20Get20";
import TeacherHeader from "../components/teacher/TeacherHeader";
import InactiveTeacherProfile from "../components/teacher/TeacherProfile/InactiveTeacherProfile";
import { StickyMobileUpcomingClassFooter } from "../components/teacher/TeacherProfile/StickyMobileUpcomingClassFooter";
import {
  About,
  ProfileSection,
  TeacherMetaTags,
} from "../components/teacher/TeacherProfile/TeacherProfileSummary";
import { UpcomingClassesModal } from "../components/teacher/TeacherProfile/UpcomingClassesModal";
import { UpcomingClassesWidget } from "../components/teacher/TeacherProfile/UpcomingClassesWidget";
import TeacherPublicScheduleCalendar from "../components/teacher/TeacherPublicScheduleCalendar";
import { UserMode } from "../lib/UserMode";
import { useFollowUserMutation } from "../queries/FollowedUserMutations";
import NotFoundPage from "./NotFoundPage";

const publicProfileQuery = gql`
  query TeacherPublicProfileQuery(
    $uid: ID
    $leader_link: String
    $userTimeZone: String!
    $skipRating: Boolean!
  ) {
    publicProfile: publicProfileNew(uid: $uid, leader_link: $leader_link) {
      uid
      leader {
        ...LeaderFragment
        showCalendar
        teacher_approved_at
        video {
          ...AttachmentFragment
        }
        classOfferingTimeRange(userTimeZone: $userTimeZone) {
          start
          end
        }
        reviewCount
        averageActivityStarRating
        successfulClassCount
        listings {
          ...ActivityListing_listing
          activity {
            hasTeacherSchedule
          }
          sections {
            start_time
            end_time
          }
        }
        educatorTiers
      }
    }
  }
  ${ActivityListingFragment}
  ${LeaderFragment}
  ${AttachmentFragment}
`;

export default function TeacherProfile({
  match: {
    params: { uidOrLink },
  },
}: $TSFixMe) {
  const location = useLocation();
  const history = useHistory();
  const analytics = useAnalytics();
  const shouldFollow = Boolean(location?.query?.follow);
  const isMobile = Screen.useIsMobile();
  const isTablet = Screen.useIsTablet();
  const { isAdmin, isLoggedIn, currentUser, userTimeZone } = useSession();
  const followUser = useFollowUserMutation();
  const { t } = useTranslation("client\\routes\\TeacherProfile");

  /** handle scroll to different sections */
  const scrollToElement = useScrollToElement();
  const reviewsRef = React.useRef(null);
  const classesRef = React.useRef(null);
  const calendarRef = React.useRef(null);

  const [upcomingCalendarView, setUpcomingCalendarView] =
    React.useState("month");

  const { error, loading, data } = useQueryWithPreviousData<
    TeacherPublicProfileQueryQuery,
    TeacherPublicProfileQueryQueryVariables
  >(publicProfileQuery, {
    variables: {
      [helpers.isUuid(uidOrLink) ? "uid" : "leader_link"]: uidOrLink,
      // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
      userTimeZone,
      skipRating: false,
    },
  });

  const { leader } = data?.publicProfile ?? {};
  const isSelfProfile = currentUser && leader && currentUser.uid === leader.uid;
  const userMode = isSelfProfile ? UserMode.Educator : UserMode.Parent;
  const { listings = [] } = leader ?? {};

  const hasLeaderBackgroundCheckExpired =
    leader && leader.hasBackgroundCheckExpired;

  const [clubListings, nonClubListings] = _.partition(
    listings,
    listing => listing.activity.isClub,
  );

  const [selfPacedListings, nonAsyncListings] = _.partition(
    nonClubListings,
    l => l.activity.isSelfPaced || l.activity.isFlexSchedule,
  );

  const [upcomingListings, otherListings] = _.partition(
    nonAsyncListings,
    l => l.activity.canEnroll,
  );

  const [upcomingGroupClassListings, upcomingTutoringListings] = _.partition(
    upcomingListings,
    l => (l.activity.size_max || 0) > 1,
  );

  React.useEffect(() => {
    (async () => {
      if (shouldFollow && isLoggedIn && leader) {
        const referer = pathFromLocation(location);
        const attribution = await analytics.attribution();

        followUser(leader, referer, attribution);

        history.replace({
          ...location,
          query: _.omit(location.query, "follow"),
        });
      }
    })();
  }, [
    analytics,
    shouldFollow,
    followUser,
    isLoggedIn,
    leader,
    location,
    history,
  ]);

  // show all reviews modal
  const [isReviewModalOpen, setIsReviewModalOpen] = React.useState(false);

  // upcoming classes modal
  const [isUpcomingClassesModalOpen, setIsUpcomingClassesModalOpen] =
    React.useState(false);

  if (loading && !data) {
    return (
      <SimpleLayout userMode={userMode} error={error} allowCategoriesNavbar>
        <Loading />
      </SimpleLayout>
    );
  } else if (!loading && !leader) {
    return <NotFoundPage />;
  } else if (
    listings.length === 0 &&
    !isAdmin && // but, don't hide from admins
    (!currentUser || !leader || currentUser.uid !== leader.uid) // nor, from myself
  ) {
    return (
      <InactiveTeacherProfile
        leader={leader}
        userMode={userMode}
        error={error}
        isMobile={isMobile}
        isTablet={isTablet}
        userTimeZone={userTimeZone}
      />
    );
  }

  // classes for upcoming classes widget
  const nextThreeUpcomingClasses = getNextThreeUpcomingClasses(
    upcomingListings,
    nonClubListings,
  );

  const hasNonClubListings = nonClubListings.length > 0;
  const hasUpcomingListings = upcomingListings.length > 0;
  const hasRequestableListings = otherListings.length > 0;

  const canParentEnroll =
    !hasLeaderBackgroundCheckExpired || Boolean(leader.ownerOfSellerOrg);

  const shouldShowUpcomingClassesModal =
    canParentEnroll &&
    // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
    (leader.showCalendar || isLoggedIn) &&
    hasUpcomingListings;

  return (
    <TrackingPage
      name="leaderProfile"
      uniqueId="leaderProfile"
      leader={leader}
      sharedProperties={{
        educatorTiers: leader?.educatorTiers.join(",") || "",
      }}
    >
      {setImpressionNode => (
        <SimpleLayout
          error={error}
          userMode={userMode}
          // @ts-ignore TS(2345): Argument of type 'HTMLDivElement | null' is not as... Remove this comment to see the full error message
          ref={node => setImpressionNode(node)}
          allowCategoriesNavbar
        >
          <Box
            sx={{
              marginBottom: "120px",
            }}
          >
            <TeacherMetaTags
              leader={leader}
              numberOfUpcomingListings={upcomingListings.length}
              numberOfOtherListings={otherListings.length}
              // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
              profileUrl={leaderUrl(leader.uid, leader.leader_link)}
            />
            {/* @ts-ignore TS(2322): Type 'ApolloError | undefined' is not assignable t... Remove this comment to see the full error message */}

            {hasNonClubListings && (
              <StickyMobileUpcomingClassFooter
                buttonText={
                  shouldShowUpcomingClassesModal
                    ? t`See Schedule`
                    : t`See Classes`
                }
                handleButtonClick={
                  shouldShowUpcomingClassesModal
                    ? () => setIsUpcomingClassesModalOpen(true)
                    : () => scrollToElement(classesRef.current)
                }
                // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
                classOfferingTimeRange={leader.classOfferingTimeRange}
                hasUpcomingListings={hasUpcomingListings}
              />
            )}
            <ProfileSection fullWidth={true} center={isMobile || isTablet}>
              <TeacherHeader
                leader={leader!}
                handleReviewsSummaryClick={() =>
                  scrollToElement(reviewsRef.current)
                }
                showActionsInColumnForLargeScreens
              />
            </ProfileSection>
            <ProfileSection>
              <Box
                flex
                sx={{
                  alignItems: "flex-start",
                  paddingY: "64px",
                }}
              >
                <About leader={leader!} userTimeZone={userTimeZone!} />
                {!isMobile && (
                  <UpcomingClassesWidget
                    upcomingClasses={nextThreeUpcomingClasses}
                    hasRequestableClasses={hasRequestableListings}
                    canViewCalendar={
                      // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
                      !leader.ownerOfSellerOrg &&
                      // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
                      (leader.showCalendar || isLoggedIn)
                    }
                    canParentEnroll={canParentEnroll}
                    handleViewCalendarClick={() =>
                      scrollToElement(calendarRef.current)
                    }
                    handleSeeMoreClick={() =>
                      scrollToElement(classesRef.current)
                    }
                  />
                )}

                <UpcomingClassesModal
                  isModalOpen={
                    isUpcomingClassesModalOpen && shouldShowUpcomingClassesModal
                  }
                  handleCloseModal={() => setIsUpcomingClassesModalOpen(false)}
                  leader={leader}
                />
              </Box>
            </ProfileSection>
            <ProfileSection
              fullWidth={isMobile ? true : false}
              ref={reviewsRef}
              hasBorder={false}
            >
              <PublicReviewsOfLeaderModal
                // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
                leaderUid={leader.uid}
                isReviewModalOpen={isReviewModalOpen}
                setIsReviewModalOpen={setIsReviewModalOpen}
                // @ts-ignore TS(2322): Type 'number | null' is not assignable to type 'nu... Remove this comment to see the full error message
                averageActivityStarRating={leader.averageActivityStarRating}
                // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
                reviewCount={leader.reviewCount}
                ReviewRespondButton={ReviewRespondButton}
              />

              <Box
                sx={{
                  paddingY: "48px",
                }}
              >
                <PublicReviewsOfLeader
                  // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
                  leaderUid={leader.uid}
                  handleOpenReviewModal={() => setIsReviewModalOpen(true)}
                />
              </Box>
              <hr />
            </ProfileSection>
            {/* @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'. */}
            {(leader.showCalendar || isLoggedIn) &&
              !isMobile &&
              // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
              !leader.ownerOfSellerOrg && (
                <ProfileSection ref={calendarRef}>
                  <Box
                    sx={{
                      marginY: "48px",
                    }}
                  >
                    <Typography variant="h3">{t`Calendar`}</Typography>

                    <Box
                      flex
                      sx={{
                        marginTop: "24px",
                        justifyContent: "end",
                      }}
                    >
                      <ViewSelector
                        views={
                          isMobile
                            ? []
                            : [ViewSelectorName.Week, ViewSelectorName.Month]
                        }
                        view={upcomingCalendarView}
                        onView={setUpcomingCalendarView}
                      />
                    </Box>
                    <TeacherPublicScheduleCalendar
                      view={upcomingCalendarView}
                      onView={setUpcomingCalendarView}
                      canParentEnroll={canParentEnroll}
                      // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
                      leaderUid={leader.uid}
                      // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
                      userTimeZone={userTimeZone}
                    />
                  </Box>
                </ProfileSection>
              )}

            <ProfileSection hasBorder={false} ref={classesRef}>
              <Box
                flex
                sx={{
                  flexDirection: "column",
                  marginY: "48px",
                }}
              >
                <Typography variant="h3">{t`Taught by this educator`}</Typography>
                {!listings.length ? (
                  <Typography
                    variant="inherit"
                    sx={{
                      paddingTop: "12px",
                    }}
                  >
                    {t`No classes available for enrollment`}
                  </Typography>
                ) : (
                  <Box>
                    <ClassList
                      upcomingGroupClassListings={upcomingGroupClassListings}
                      upcomingTutoringListings={upcomingTutoringListings}
                      selfPacedListings={selfPacedListings.filter(
                        l => l.activity.canEnroll,
                      )}
                      otherListings={otherListings}
                      clubListings={clubListings}
                    />
                  </Box>
                )}
              </Box>
            </ProfileSection>
          </Box>
        </SimpleLayout>
      )}
    </TrackingPage>
  );
}

const LeaderActions = ({
  leader,
  location,
  currentUser,
  isLoggedIn,
  isAdmin,
  isVertical = false,
}: $TSFixMe) => {
  const trackAdEvent = useTrackEvent(IntegrationCategory.Advertising);
  const isCurrentUserFollowingLeader =
    useIsTeacherFollowedByCurrentUser(leader);
  const { t } = useTranslation("client\\routes\\TeacherProfile");
  const editLink = currentUser && leader && currentUser.uid === leader.uid && (
    <Box
      sx={{
        marginTop: "16px",
        textAlign: "center",
      }}
    >
      <Link to={Routes.leaderSettingsPath()}>{t`Edit your profile`}</Link>
    </Box>
  );
  const leaderName = leader.name;

  const [showShareModal, setShowShareModal] = useState(false);
  const onlyCreditAmount = renderCurrencyString({
    priceInCents: REFER_CREDIT_CENTS,
    showCurrencyCode: false,
  });
  const title = t(
    "Share this teacher with a friend - new users will get {{onlyCreditAmount}} off",
    {
      onlyCreditAmount: onlyCreditAmount,
    },
  );
  const subTitle = t(
    "You’ll also get {{onlyCreditAmount}} when they sign up with your link and take their first class!",
    {
      onlyCreditAmount: onlyCreditAmount,
    },
  );
  const shareLink = addParamsToUrl(leaderUrl(leader.uid, leader.leader_link), {
    signup: true,
    usid: currentUser?.slug_id,
  });

  const isEducator =
    !!currentUser?.teacher_approved_at && !currentUser?.suspended_at;

  return (
    <div>
      <Box
        flex
        sx={{
          flexDirection: "column",
        }}
      >
        <Box
          flex
          sx={{
            flexDirection: isVertical ? "column" : "row",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: isVertical ? "stretch" : "start",
            columnGap: "16px",
            rowGap: isVertical ? "16px" : "8px",
          }}
        >
          <FollowButton
            size={isVertical ? "medium" : "small"}
            data-test-id="teacher-profile-follow-button"
            isSet={isCurrentUserFollowingLeader}
            leader={leader}
          />
          <MessageModal
            renderButton={(open: $TSFixMe) => (
              <Button
                variant="outlined"
                onClick={open}
                size={isVertical ? "medium" : "small"}
                startIcon={
                  <Icon
                    icon={faEnvelope}
                    size="small"
                    sx={{ fontSize: "14px !important" }}
                  />
                }
              >
                {t`Message`}
              </Button>
            )}
            user={currentUser}
            location={location}
            requireLogin={true}
            isLoggedIn={isLoggedIn}
            viewName="sendMessageToLeader"
            heading={t`Ask the teacher`}
            prompt={(close: $TSFixMe) => (
              <React.Fragment>
                <Trans t={t}>
                  Ask {{ leaderName }} about their classes or background. For
                  questions about the Outschool website,{" "}
                  <HelpLink onClick={close} sx={{ width: "fit-content" }}>
                    use Outschool Help
                  </HelpLink>
                  .
                </Trans>
              </React.Fragment>
            )}
            recipients={[leader]}
            onMessageCreated={() => {
              trackAdEvent("Message Teacher");
            }}
            showFromAndTo
            subject={t("Question for {{leaderName}}", { leaderName })}
            context={{
              action: t`Ask a Question`,
              leaderUid: leader.uid,
              leaderName: leader.name,
              subject: t("Question for {{leaderName}}", { leaderName }),
            }}
          />
          <Button
            color="primary"
            size={isVertical ? "medium" : "small"}
            variant="outlined"
            onClick={() => {
              setShowShareModal(true);
            }}
            sx={{
              marginBottom: 10,
            }}
          >
            <Icon
              icon={faShare}
              sx={{
                marginRight: "0.5em",
              }}
            />{" "}
            {t("Share")}
          </Button>
          {isEducator ? (
            <OutreachModal
              open={showShareModal}
              onClose={() => setShowShareModal(false)}
              outreachShareUrl={shareLink}
              modalType={{
                variant: OutreachModalType.teacher,
                teacher: leader,
              }}
            />
          ) : (
            <PageModal
              open={showShareModal}
              onClose={() => setShowShareModal(false)}
              fullBleed={false}
              sx={{ maxWidth: 450, padding: "medium" }}
            >
              <OutReachOrGive20Get20
                title={title}
                supportingCopy={subTitle}
                link={shareLink}
              />
            </PageModal>
          )}
        </Box>
        {editLink}

        {isAdmin && (
          <Box
            sx={{
              marginTop: "16px",
              textAlign: "center",
            }}
          >
            <Link to={Routes.userAdminPath(leader)}>
              {/* eslint-disable-next-line i18next/no-literal-string */}
              <span role="img" aria-label="top hat">
                🎩
              </span>{" "}
              {t`Admin`}
            </Link>
          </Box>
        )}
      </Box>
    </div>
  );
};

LeaderActions.displayName = "LeaderActions";

const fadeInAnimation = keyframes({
  from: {
    opacity: 0,
  },
  to: {
    opacity: 1,
  },
});

/** Tracking */
type TeacherProfileTrackingArea =
  | "Classes"
  | "Groups"
  | "Upcoming Classes Widget"
  | "Upcoming Classes Modal";

type PartialActivity = Partial<
  Pick<
    ActivityType,
    | "uid"
    | "age_max"
    | "age_min"
    | "averageStarRating"
    | "duration_minutes"
    | "duration_weeks"
    | "isClub"
    | "is_ongoing_weekly"
    | "price_cents"
    | "reviewCount"
    | "weekly_meetings"
  >
>;

type PartialSection = Partial<
  Pick<
    SectionType,
    "uid" | "filledSpaceCount" | "size_max" | "size_min" | "start_time"
  >
>;

type PartialListing = Pick<
  ListingCard_ListingFragment,
  "sectionCount" | "openSectionCount" | "upcomingSectionCount"
>;

type PartialLeader = Partial<
  Pick<Leader, "averageActivityStarRating" | "reviewCount">
>;

export const getTrackingObject = ({
  area,
  activity,
  section,
  listing,
  leader,
  trackingProperties = {},
}: {
  area: TeacherProfileTrackingArea;
  activity: PartialActivity;
  section?: PartialSection | null;
  listing?: PartialListing;
  leader?: PartialLeader;
  trackingProperties?: Record<string, any>;
}) => {
  const activityTrackingProps: ActivityTrackingProps = {
    activity_uid: activity?.uid ?? null,
    activity_age_max: activity?.age_max ?? null,
    activity_age_min: activity?.age_min ?? null,
    activity_average_star_rating: activity?.averageStarRating ?? null,
    activity_duration_minutes: activity?.duration_minutes ?? null,
    activity_duration_weeks: activity?.duration_weeks ?? null,
    activity_is_club: activity?.isClub?.toString() ?? null,
    activity_is_ongoing_weekly: activity?.is_ongoing_weekly?.toString() ?? null,
    activity_price_cents: activity?.price_cents ?? null,
    activity_review_count: activity?.reviewCount ?? null,
    activity_weekly_meetings: activity?.weekly_meetings ?? null,
    first_section_filled_space_count: section?.filledSpaceCount ?? null,
    first_section_size_max: section?.size_max ?? null,
    first_section_start_time: section?.start_time ?? null,
    leader_average_activity_star_rating:
      leader?.averageActivityStarRating ?? null,
    leader_review_count: leader?.reviewCount ?? null,
    section_uid: section?.uid ?? null,
  };

  return {
    trackingName: "Activity Impression",
    trackingTouchEventName: "Activity Impression Click",
    trackingViewEventName: "Activity Impression",
    trackingUniqueId: activity?.uid,
    area,
    sharedProperties: {
      ...activityTrackingProps,
      section_count: listing?.sectionCount ?? null,
      open_section_count: listing?.openSectionCount ?? null,
      upcoming_section_count: listing?.upcomingSectionCount ?? null,
      ...trackingProperties,
    },
  };
};

const getGroupTrackingProperties = (listing: $TSFixMe) =>
  getTrackingObject({
    area: "Groups",
    activity: listing.activity,
    section: listing.nextSection,
    leader: listing.leader,
    listing: {
      sectionCount: listing.sectionCount,
      openSectionCount: listing.openSectionCount,
      upcomingSectionCount: listing.upcomingSectionCount,
    },
  }).sharedProperties;

interface ActivityListingCardProps {
  listing: ActivityListing_ListingFragment;
}

const ActivityListingCard: React.FunctionComponent<
  ActivityListingCardProps
> = ({ listing }) => {
  const { activity } = listing;
  const trackProductClicked = useProductClicked();
  const isActivitySavedByCurrentUser = useIsActivitySavedByCurrentUser(
    listing.activity,
  );
  const { isLoggedIn } = useSession();
  const isMobile = Screen.useIsMobile();
  const [screenWidth] = Screen.useWindowSize();
  const { t } = useTranslation("client\\routes\\TeacherProfile");

  const linkTo = React.useMemo(
    () => createPathWithActivity(activity),
    [activity],
  );

  const navigate = useNavigation();
  const customOnClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (!isLoggedIn) {
        e.preventDefault();
        // Hard navigate so a full page load opens SSR.
        navigate(linkTo, {
          hardNav: true,
        });
      }
      // @ts-ignore TS(2345): Argument of type 'ActivityListing_ListingFragment'... Remove this comment to see the full error message
      trackProductClicked(listing);
    },
    [isLoggedIn, linkTo, navigate, trackProductClicked, listing],
  );
  return (
    <Link
      to={linkTo}
      {...getTrackingObject({
        area: "Classes",
        activity: listing.activity,
        section: listing?.nextSection,
        leader: listing.leader,
      })}
      useLinkTracking
      onClick={customOnClick}
      trackViews={true}
    >
      <Box
        flex
        sx={{
          flexDirection: "column",
          position: "relative",
        }}
      >
        <Image
          src={FilestackUrls.imageUrl(activity.details.photo, {
            width: Math.min(screenWidth / 2, 504),
          })}
          sx={{
            borderRadius: "16px",
            marginBottom: "8px",
            animation: `${fadeInAnimation} 2s`,
            maxWidth: "100%",
            height: "auto",
          }}
        />
        <Box
          flex
          sx={{
            height: isMobile ? "32px" : "40px",
            width: isMobile ? "32px" : "40px",
            borderRadius: "20px",
            backgroundColor: "common.white",
            position: "absolute",
            top: "12px",
            right: 12,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <SaveHeart
            sx={{ fontSize: isMobile ? "16px" : "20px" }}
            isSet={isActivitySavedByCurrentUser}
            activity={activity}
            // @ts-ignore TS(2322): Type '{ __typename?: "Leader" | undefined; uid: st... Remove this comment to see the full error message
            leader={listing.leader}
          />
        </Box>
        <Box
          flex
          sx={{
            flexDirection: "row",
          }}
        >
          <Icon
            icon={faCalendarAlt}
            sx={{
              color: "#5c6f8b",
              marginRight: "0.5em",
              fontSize: "17px",
            }}
          />
          <Typography
            variant="inherit"
            sx={{
              color: "grey.500",
              fontSize: "14px",
            }}
          >
            {scheduleText(activity, t)[0]}
          </Typography>
        </Box>
        <Typography
          variant="inherit"
          sx={{
            display: "block",
          }}
        >
          {activity.titleTranslated}
        </Typography>
        <Typography
          variant="inherit"
          sx={{
            color: "grey.500",
            fontSize: "14px",
          }}
        >
          {t("Ages {{ageMin}} - {{ageMax}}", {
            ageMin: activity.age_min,
            ageMax: activity.age_max,
          })}
        </Typography>
      </Box>
    </Link>
  );
};

interface ClassListProps {
  upcomingGroupClassListings: ActivityListing_ListingFragment[];
  upcomingTutoringListings: ActivityListing_ListingFragment[];
  selfPacedListings: ActivityListing_ListingFragment[];
  clubListings: ActivityListing_ListingFragment[];
  otherListings: ActivityListing_ListingFragment[];
}

enum ClassListTabOption {
  GroupClass = "GROUP_CLASS",
  Tutoring = "TUTORING",
  Club = "CLUB",
  SelfPaced = "SELF_PACED",
  Other = "OTHER",
}

const ClassList: React.FunctionComponent<ClassListProps> = ({
  upcomingGroupClassListings,
  upcomingTutoringListings,
  selfPacedListings,
  otherListings,
  clubListings,
}: ClassListProps) => {
  const { t } = useTranslation("client\\routes\\TeacherProfile");
  const translateTabName = React.useCallback(
    (tabOption: ClassListTabOption) => {
      switch (tabOption) {
        case ClassListTabOption.GroupClass:
          return t("Live Group Classes");
        case ClassListTabOption.Tutoring:
          return t("1-on-1 Tutoring");
        case ClassListTabOption.SelfPaced:
          return t("Self-Paced Classes");
        case ClassListTabOption.Club:
          return t("Chat Groups");
        case ClassListTabOption.Other:
          return t("By Request");
        default:
          return tabOption;
      }
    },
    [t],
  );

  const [impressionNode, setImpressionNode] = React.useState();
  useImpressionTracking({
    node: impressionNode,
    trackingLabel: "teacher_class_list_section",
    uniqueId: "TeacherClassListSection",
    trackViews: true,
  });
  const trackEvent = useTrackEvent();

  const trackProductListViewed = useProductListViewed();
  const [activeTab, setActiveTab] = React.useState(
    getActiveTab({
      clubListings,
      selfPacedListings,
      upcomingGroupClassListings,
      upcomingTutoringListings,
    }),
  );
  const activeList = getActiveTabList(activeTab, {
    upcomingGroupClassListings,
    upcomingTutoringListings,
    selfPacedListings,
    otherListings,
    clubListings,
  });
  React.useEffect(() => {
    // @ts-ignore TS(2345): Argument of type 'ActivityListing_ListingFragment[... Remove this comment to see the full error message
    trackProductListViewed("educator-class-list", activeTab, activeList);
  }, [activeList, activeTab, trackProductListViewed]);

  const shouldUseGridLayout = activeTab !== ClassListTabOption.Club;

  return (
    <Box
      sx={{
        marginTop: "30px",
      }}
      ref={setImpressionNode}
    >
      <NavTabs.TabList
        sx={{
          marginBottom: "30px",
          borderBottom: "1px solid #DDDFE3 ",
        }}
      >
        <ClassListTab
          tabName={translateTabName(ClassListTabOption.GroupClass) as any}
          isActive={activeTab === ClassListTabOption.GroupClass}
          itemCount={upcomingGroupClassListings.length}
          onClick={e => {
            e.preventDefault();
            setActiveTab(ClassListTabOption.GroupClass);
            trackEvent("teacher_class_list_touch", {
              tab: ClassListTabOption.GroupClass,
            });
          }}
        ></ClassListTab>
        <ClassListTab
          tabName={translateTabName(ClassListTabOption.Tutoring) as any}
          isActive={activeTab === ClassListTabOption.Tutoring}
          itemCount={upcomingTutoringListings.length}
          onClick={e => {
            e.preventDefault();
            setActiveTab(ClassListTabOption.Tutoring);
            trackEvent("teacher_class_list_touch", {
              tab: ClassListTabOption.Tutoring,
            });
          }}
        ></ClassListTab>
        <ClassListTab
          tabName={translateTabName(ClassListTabOption.SelfPaced) as any}
          isActive={activeTab === ClassListTabOption.SelfPaced}
          itemCount={selfPacedListings.length}
          onClick={e => {
            e.preventDefault();
            setActiveTab(ClassListTabOption.SelfPaced);
            trackEvent("teacher_class_list_touch", {
              tab: ClassListTabOption.SelfPaced,
            });
          }}
        ></ClassListTab>
        <ClassListTab
          tabName={translateTabName(ClassListTabOption.Club) as any}
          isActive={activeTab === ClassListTabOption.Club}
          itemCount={clubListings.length}
          onClick={e => {
            e.preventDefault();
            setActiveTab(ClassListTabOption.Club);
            trackEvent("teacher_class_list_touch", {
              tab: ClassListTabOption.Club,
            });
          }}
        ></ClassListTab>
        <ClassListTab
          tabName={translateTabName(ClassListTabOption.Other) as any}
          isActive={activeTab === ClassListTabOption.Other}
          itemCount={otherListings.length}
          onClick={e => {
            e.preventDefault();
            setActiveTab(ClassListTabOption.Other);
            trackEvent("teacher_class_list_touch", {
              tab: ClassListTabOption.Other,
            });
          }}
        ></ClassListTab>
      </NavTabs.TabList>
      <ClassListLayout isGrid={shouldUseGridLayout}>
        {activeTab === ClassListTabOption.GroupClass &&
          upcomingGroupClassListings.map(listing => (
            <ActivityListingCard key={listing.activity.uid} listing={listing} />
          ))}
        {activeTab === ClassListTabOption.Tutoring &&
          upcomingTutoringListings.map(listing => (
            <ActivityListingCard key={listing.activity.uid} listing={listing} />
          ))}
        {activeTab === ClassListTabOption.SelfPaced &&
          selfPacedListings.map(listing => (
            <ActivityListingCard key={listing.activity.uid} listing={listing} />
          ))}
        {activeTab === ClassListTabOption.Club &&
          clubListings.map((listing, idx) => (
            <ActivityListing
              key={idx.toString()}
              listing={listing}
              trackingProperties={getGroupTrackingProperties(listing)}
            />
          ))}
        {activeTab === ClassListTabOption.Other &&
          otherListings.map(listing => (
            <ActivityListingCard key={listing.activity.uid} listing={listing} />
          ))}
      </ClassListLayout>
    </Box>
  );
};

function getActiveTab({
  clubListings,
  selfPacedListings,
  upcomingGroupClassListings,
  upcomingTutoringListings,
}: {
  clubListings: ActivityListing_ListingFragment[];
  selfPacedListings: ActivityListing_ListingFragment[];
  upcomingGroupClassListings: ActivityListing_ListingFragment[];
  upcomingTutoringListings: ActivityListing_ListingFragment[];
}): ClassListTabOption {
  return upcomingGroupClassListings.length > 0
    ? ClassListTabOption.GroupClass
    : upcomingTutoringListings.length > 0
    ? ClassListTabOption.Tutoring
    : selfPacedListings.length > 0
    ? ClassListTabOption.SelfPaced
    : clubListings.length > 0
    ? ClassListTabOption.Club
    : ClassListTabOption.Other;
}

function getActiveTabList(
  activeTab: ClassListTabOption,
  {
    upcomingGroupClassListings,
    upcomingTutoringListings,
    selfPacedListings,
    otherListings,
    clubListings,
  }: {
    upcomingGroupClassListings: ActivityListing_ListingFragment[];
    upcomingTutoringListings: ActivityListing_ListingFragment[];
    selfPacedListings: ActivityListing_ListingFragment[];
    clubListings: ActivityListing_ListingFragment[];
    otherListings: ActivityListing_ListingFragment[];
  },
): ActivityListing_ListingFragment[] {
  let products: ActivityListing_ListingFragment[];

  switch (activeTab) {
    case ClassListTabOption.GroupClass:
      products = upcomingGroupClassListings;
      break;
    case ClassListTabOption.Tutoring:
      products = upcomingTutoringListings;
      break;
    case ClassListTabOption.SelfPaced:
      products = selfPacedListings;
      break;
    case ClassListTabOption.Club:
      products = clubListings;
      break;
    case ClassListTabOption.Other:
      products = otherListings;
      break;
    default:
      products = [];
  }

  return products;
}

function getNextThreeUpcomingClasses(
  upcomingListings: Array<ActivityListing_ListingFragment>,
  fallbackListings: Array<ActivityListing_ListingFragment>,
) {
  const nextThreeUpcomingClasses = _.orderBy(upcomingListings, [
    l =>
      !!l.activity.is_ongoing_weekly
        ? new Date(
            l.nextSection?.nextOngoingMeeting?.start_time || 999999999999999,
          )
        : new Date(l.nextSection?.start_time || 999999999999999),
  ]).slice(0, 3);

  if (nextThreeUpcomingClasses.length === 3) {
    return nextThreeUpcomingClasses;
  }

  const teacherScheduleActivities = fallbackListings
    .filter(
      l =>
        (l.activity.hasTeacherSchedule ||
          l.activity.isSelfPaced ||
          l.activity.isFlexSchedule) &&
        l.activity.published_at,
    )
    .sort((a, b) =>
      a.activity.published_at > b.activity.published_at ? -1 : 1,
    );

  if (!teacherScheduleActivities.length) {
    return nextThreeUpcomingClasses;
  }

  const fillCount =
    Math.min(3, teacherScheduleActivities.length) -
    nextThreeUpcomingClasses.length;

  for (let i = 0; i < fillCount; i++) {
    nextThreeUpcomingClasses.push(teacherScheduleActivities[i]);
  }

  return nextThreeUpcomingClasses;
}

interface ClassListTabProps {
  tabName: ClassListTabOption;
  itemCount: number;
  isActive: boolean;
  onClick: (e: React.SyntheticEvent<Element, Event>) => void;
}
const ClassListTab: React.FunctionComponent<ClassListTabProps> = ({
  tabName,
  itemCount,
  isActive,
  onClick,
}) => {
  const isMobile = Screen.useIsMobile();
  const marginRight = isMobile ? "1em" : "2em";
  return (
    <NavTabs.UnderlinedTab
      to={"#classes"}
      isActive={isActive}
      onClick={onClick}
      linkSx={{
        marginRight,
      }}
    >
      <Box
        component="span"
        sx={{
          fontWeight: isActive ? "800" : "unset",
        }}
      >
        {" "}
        {tabName} ({itemCount})
      </Box>
    </NavTabs.UnderlinedTab>
  );
};

interface ClassListLayoutProps {
  isGrid: boolean;
}

const ClassListLayout: React.FunctionComponent<
  React.PropsWithChildren<ClassListLayoutProps>
> = ({ children, isGrid }) => {
  const isMobile = Screen.useIsMobile();
  if (isGrid) {
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "repeat(auto-fit, minmax(150px, 1fr))"
            : "repeat(auto-fit, minmax(200px, 0.5fr))",
          columnGap: isMobile ? "12px" : "16px",
          rowGap: isMobile ? "24px" : "48px",
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
      }}
    >
      {children}
    </Box>
  );
};
